/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-01-20 18:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Badge, Link, ButtonGroup, Button, Icon, Tooltip } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { EditMinor, DeleteMinor, DuplicateMinor } from '@shopify/polaris-icons';
import StrategyType from './strategy-type';
import LoadingRows from 'src/components/views/merchandising-v2/loading-rows';
import ConfirmDeleteModal from 'src/components/views/merchandising-v2/confirm-delete-modal';

import { displayDate } from 'src/helpers/date-time-helper';
import { DATE_FORMAT } from 'src/constants/language-setting';
import { capitalizeFirstLetter } from 'src/helpers';
import { enterSABFullscreen } from 'src/integrations/shopify-app-bridge/fullscreen';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import { getStrategyField } from 'src/helpers/merchandising-v2-helper';
import { FavoriteMajor } from '@shopify/polaris-icons';

type Props = {
  columns: Array<string>,
  loading: boolean,
  deleting: boolean,
  rules: Array<any>,
  handleChangeRuleStatus: () => void,
  deleteRule: () => void,
  afterDeleteRule: (id: string) => void,
  appLanguage: string,
  total: number
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalRemove: boolean,
  index: number,
  selectedId: string
};

class ListRules extends Component<ComposedProps, State> {
  state = {
    activeModalRemove: false,
    index: -1,
    selectedId: ''
  };

  componentWillUpdate(nextProps, nextState) {
    const { deleting, success } = nextProps;
    const { deleting: deletingPrev } = this.props;
    const { selectedId } = this.state;

    if (success === true && deleting !== deletingPrev) {
      this.handleActiveOptionModalRemove();
      this.props.afterDeleteRule(selectedId);
      this.setState({ selectedId: '' });
    }
  }

  render() {
    const { rules, loading, columns, i18n } = this.props;
    const displayColumns = columns.map(e => e.value);

    return (
      <>
        <div className="Polaris-DataTable">
          <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
            <table className="Polaris-DataTable__Table">
              <thead>
                <tr>
                  {_.map(displayColumns, (column, index) => (
                    <th
                      key={`${column}-${index}`}
                      className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--${column}`}>
                      <p data-tut={column}>
                        {i18n.translate(
                          `AdminHTML.MerchandisingV2.ListRules.tableHeading_${capitalizeFirstLetter(
                            column,
                            'en'
                          )}`
                        )}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              {!_.isEmpty(rules) ? (
                <tbody>
                  {rules.map((item, key) => {
                    return (
                      <tr className="Polaris-DataTable__TableRow" key={`item-${item?.id}-${key}`}>
                        {_.map(displayColumns, (column, index) =>
                          this.renderContent(item, column, index)
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              ) : (
                <tbody>
                  {loading && (
                    // Loading rows while reading merchandising rules
                    <LoadingRows />
                  )}
                </tbody>
              )}
            </table>
          </div>
        </div>

        <ConfirmDeleteModal
          active={this.state.activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteRule}
          deleting={this.props.deleting}
          title={i18n.translate('AdminHTML.MerchandisingV2.ListRules.confirmDeleteModal_Title')}
          content={i18n.translate('AdminHTML.MerchandisingV2.ListRules.confirmDeleteModal_Content')}
        />
      </>
    );
  }

  /* #region: Render content components */
  renderContent = (item, column, index) => {
    switch (column) {
      case 'status':
        return this.renderStatus(item, index);
      case 'queries':
        return this.renderQueries(item, index);
      case 'collections':
        return this.renderCollections(item, index);
      case 'strategies':
        return this.renderMerchandisingStrategies(item, index);
      case 'dateRange':
        return this.renderDateRange(item, index);
      case 'actions':
        return this.renderActions(item, index);
      default:
        return '';
    }
  };

  // Merchandising rule status
  renderStatus = (item, index) => {
    const { i18n, notAllowAddonMerchandising, lockRuleCollectionPage } = this.props;
    const statusDisplay =
      item.status === 'enabled'
        ? i18n.translate('AdminHTML.MerchandisingV2.ListRules.statusEnable')
        : i18n.translate('AdminHTML.MerchandisingV2.ListRules.statusDisable');

    if (notAllowAddonMerchandising || item.lock) {
      return (
        <td
          className="Polaris-DataTable__Cell Polaris-DataTable__Cell--status  Polaris-DataTable__Cell--status-locked"
          key={`status-${item?.id}-${index}`}>
          <label className="BC-Checkbox-Status">
            <input
              type="checkbox"
              disabled
              checked={lockRuleCollectionPage ? item.status === 'enabled' : false}
            />
            <span />
          </label>
        </td>
      );
    }

    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--status"
        key={`status-${item?.id}-${index}`}>
        <Tooltip content={statusDisplay}>
          <label className="BC-Checkbox-Status">
            {this.props.saving ? (
              <input type="checkbox" checked={item.status === 'enabled'} />
            ) : (
              <input
                type="checkbox"
                checked={item.status === 'enabled'}
                onChange={e => this.updateStatus(e, item.id)}
              />
            )}

            <span />
          </label>
        </Tooltip>
      </td>
    );
  };

  // Merchandising queries
  renderQueries = (item, index) => {
    const { i18n } = this.props;
    const isUniversal = _.get(item, 'isUniversal', false);

    const { lock = false } = item || {};
    if (isUniversal) {
      return (
        <td
          key={`queries-${item?.id}-${index}`}
          className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--queries${
            item.status === 'disabled' ? '-disabled' : ''
          }`}>
          <div style={{ display: 'flex' }}>
            {lock ? (
              <div style={{ color: 'rgba(140, 145, 150, 1)' }}>
                {i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueries')}
              </div>
            ) : (
              <Link
                removeUnderline
                onClick={() => {
                  this.handleEditRule(item.id, item.ruleType);
                }}>
                {i18n.translate('AdminHTML.MerchandisingV2.Rule.allQueries')}
              </Link>
            )}
            <Tooltip
              preferredPosition="above"
              content={i18n.translate('AdminHTML.MerchandisingV2.ListRules.allRuleTooltip')}>
              <div className="wrapTypeAllIcon">
                <span className="typeAllIcon">
                  <Icon source={FavoriteMajor} color="base" />
                </span>
              </div>
            </Tooltip>
          </div>
        </td>
      );
    }

    const queries = _.get(item, 'queries', []);
    let queriesString = queries.toString();
    queriesString = queriesString.replaceAll(',', ', ');

    let queryDisplay = queriesString;
    if (queries.length > 3) {
      queryDisplay = `${queries[0]}, ${queries[1]}, ${queries[2]}...`;
    }
    return (
      <td
        className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--queries${
          item.status === 'disabled' ? '-disabled' : ''
        }`}
        key={`queries-${item?.id}-${index}`}>
        {lock ? (
          <div style={{ color: 'rgba(140, 145, 150, 1)' }}>{queryDisplay}</div>
        ) : (
          <Tooltip preferredPosition="above" content={queries.length > 3 ? queriesString : ''}>
            <Link
              removeUnderline
              onClick={() => {
                this.handleEditRule(item.id, item.ruleType);
              }}>
              {queryDisplay}
            </Link>
          </Tooltip>
        )}
      </td>
    );
  };

  // Merchandising collections
  renderCollections = (item, index) => {
    const { collection = {}, ruleType, isUniversal = false, lock = false } = item;
    const { i18n } = this.props;
    const { notAllowAddonMerchandising } = this.props;
    const isDisable = notAllowAddonMerchandising || lock;

    if (isUniversal) {
      return (
        <td
          className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--collections${
            item.status === 'disabled' ? '-disabled' : ''
          } ${isDisable ? 'text-disable' : ''}`}
          key={`collections-${item?.id}-${index}`}>
          <div style={{ display: 'flex' }}>
            {isDisable ? (
              i18n.translate('AdminHTML.MerchandisingV2.Rule.allCollect')
            ) : (
              <Link
                removeUnderline
                onClick={() => {
                  this.handleEditRule(item.id, item.ruleType);
                }}>
                {i18n.translate('AdminHTML.MerchandisingV2.Rule.allCollect')}
              </Link>
            )}

            <Tooltip
              preferredPosition="above"
              content={i18n.translate('AdminHTML.MerchandisingV2.ListRules.allCollectionTooltip')}>
              <div className="wrapTypeAllIcon">
                <span className="typeAllIcon">
                  <Icon source={FavoriteMajor} color="base" />
                </span>
              </div>
            </Tooltip>
          </div>
        </td>
      );
    }

    return (
      <td
        className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--collections${
          item.status === 'disabled' ? '-disabled' : ''
        } ${isDisable ? 'text-disable' : ''}`}
        key={`collections-${item?.id}-${index}`}>
        {isDisable ? (
          collection?.label
        ) : (
          <Link
            removeUnderline
            onClick={() => {
              this.handleEditRule(item.id, ruleType);
            }}>
            {collection?.label}
          </Link>
        )}
      </td>
    );
  };

  // Merchandising strategies
  renderMerchandisingStrategies = (item, index) => {
    const { panelID, showRankingStrategy, notAllowAddonMerchandising } = this.props;
    const queriesType = _.get(item, 'queriesType', 'specific');
    const { lock = false } = item || {};
    const isDisable = notAllowAddonMerchandising || lock;
    let strategyTypeList = getStrategyField(panelID, queriesType, 'list');
    if (!showRankingStrategy) {
      strategyTypeList = strategyTypeList.filter(type => type !== 'productPerformanceRanking');
    }

    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--strategies"
        key={`strategies-${item?.id}-${index}`}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            width: '100%',
            gap: '10px'
          }}>
          {strategyTypeList.map((s, index) => {
            return (
              <StrategyType
                key={`list-strategy-type-${index}`}
                isDisable={isDisable}
                queries={item.queries}
                strategies={item.strategies}
                isDisabledPerformanceRanking={item.isDisabledPerformanceRanking}
                type={s}
                ruleId={item.id}
              />
            );
          })}
        </div>
      </td>
    );
  };

  // Merchandising rule date range
  renderDateRange = (item, index) => {
    const { appLanguage, i18n, notAllowAddonMerchandising } = this.props;
    const { effectFrom: from, effectTo: to, lock = false } = item;

    const isDisable = notAllowAddonMerchandising || lock;
    if (!from && !to) {
      return (
        <td
          className="Polaris-DataTable__Cell Polaris-DataTable__Cell--dateRange"
          key={`dateRange-${item?.id}-${index}`}>
          <div className={isDisable ? 'text-disable' : ''}>
            {i18n.translate('AdminHTML.MerchandisingV2.ListRules.tableContent_NoDateRange')}
          </div>
        </td>
      );
    }

    const dateFrom = from !== 0 ? displayDate(from, DATE_FORMAT[appLanguage].longDate) : 0;
    const dateTo = to !== 0 ? displayDate(to, DATE_FORMAT[appLanguage].longDate) : 0;

    const today = Math.round(new Date().getTime() / 1000);
    const isExpired = to !== 0 ? to < today : false;
    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--dateRange"
        key={`dateRange-${item?.id}-${index}`}>
        <div className={isDisable ? 'text-disable' : ''}>
          {dateFrom && dateTo && dateFrom === dateTo ? (
            dateFrom
          ) : (
            <>
              {dateFrom && (
                <>
                  {i18n.translate('AdminHTML.MerchandisingV2.ListRules.fromDate', { t1: dateFrom })}
                </>
              )}
              {dateTo && <span> {dateTo}</span>}
            </>
          )}
          {isExpired && (
            <div style={{ marginTop: '0.5rem' }}>
              <Badge>{i18n.translate('AdminHTML.MerchandisingV2.ListRules.expired')}</Badge>
            </div>
          )}
        </div>
      </td>
    );
  };

  // Merchandising actions
  renderActions = (item, index) => {
    const { panelID, notAllowAddonMerchandising, lockRuleCollectionPage } = this.props;
    const isUniversal = _.get(item, 'isUniversal', false);
    const { lock = false } = item || {};
    const isDisable = notAllowAddonMerchandising || lock;

    return (
      <td
        className="Polaris-DataTable__Cell Polaris-DataTable__Cell--actions"
        key={`actions-${item?.id}-${index}`}>
        <ButtonGroup segmented>
          <Button
            size="slim"
            disabled={isDisable}
            icon={EditMinor}
            onClick={() => this.handleEditRule(item.id, item.ruleType)}
          />
          {panelID === 'search' && !isUniversal && (
            <Button
              size="slim"
              disabled={lock}
              onClick={() => this.handleDuplicate(item.id, item.ruleType)}>
              <Icon source={DuplicateMinor} />
            </Button>
          )}
          <Button
            disabled={(notAllowAddonMerchandising && !lockRuleCollectionPage) || lock}
            size="slim"
            onClick={() => this.handleDelete(item.id)}>
            <Icon source={DeleteMinor} />
          </Button>
        </ButtonGroup>
      </td>
    );
  };
  /* #end-region */

  // Handle action edit rule
  handleEditRule = (ruleId, ruleType) => {
    history.push({
      pathname: routes.merchandising.edit(ruleId)
    });
    enterSABFullscreen();
  };

  // Handle open/close modal delete
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  /**
   * Active/Disable rule
   */
  updateStatus = (e, ruleId) => {
    this.props.handleChangeRuleStatus(ruleId);
  };

  handleDelete = ruleId => {
    this.setState({ selectedId: ruleId });
    this.handleActiveOptionModalRemove();
  };

  /**
   * Delete rule
   */
  deleteRule = () => {
    const { selectedId } = this.state;
    this.props.deleteRule(selectedId);
  };

  /**
   * Duplicate rule
   */
  handleDuplicate = (ruleId, ruleType) => {
    this.props.handleDuplicate(ruleId, ruleType);
  };
}

export default withI18n()(ListRules);
